import { useState, useMemo, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import {
  Clock,
  CaretUp,
  CaretDown,
  Pill,
  FirstAid,
  User,
  Warning,
  CheckCircle,
  Timer,
  ArrowsOutSimple,
  ArrowsInSimple,
  Stethoscope,
  Heartbeat,
  Users
} from '@phosphor-icons/react';

// Timing status definitions
export const TIMING_STATUS = {
  ON_TIME: {
    id: 'on_time',
    label: 'A Tiempo',
    color: 'bg-green-500',
    textColor: 'text-green-400',
    borderColor: 'border-green-500',
    bgLight: 'bg-green-500/10',
    icon: CheckCircle,
    description: 'Acción realizada dentro de la ventana óptima'
  },
  LATE: {
    id: 'late',
    label: 'Tardío',
    color: 'bg-yellow-500',
    textColor: 'text-yellow-400',
    borderColor: 'border-yellow-500',
    bgLight: 'bg-yellow-500/10',
    icon: Clock,
    description: 'Acción realizada fuera de la ventana óptima pero aceptable'
  },
  CRITICAL: {
    id: 'critical',
    label: 'Crítico',
    color: 'bg-orange-500',
    textColor: 'text-orange-400',
    borderColor: 'border-orange-500',
    bgLight: 'bg-orange-500/10',
    icon: Warning,
    description: 'Retraso significativo que puede afectar el resultado'
  },
  OUT_OF_WINDOW: {
    id: 'out_of_window',
    label: 'Fuera de Ventana',
    color: 'bg-red-500',
    textColor: 'text-red-400',
    borderColor: 'border-red-500',
    bgLight: 'bg-red-500/10',
    icon: Warning,
    description: 'Acción realizada muy tarde - posible evento adverso'
  }
};

// Role definitions with colors
const ROLE_STYLES = {
  medico: { label: 'Médico', color: 'text-blue-400', bgColor: 'bg-blue-500/20' },
  enfermera: { label: 'Enfermera', color: 'text-green-400', bgColor: 'bg-green-500/20' },
  tens: { label: 'TENS', color: 'text-purple-400', bgColor: 'bg-purple-500/20' },
  paramedico: { label: 'Paramédico', color: 'text-orange-400', bgColor: 'bg-orange-500/20' },
  equipo: { label: 'Equipo', color: 'text-cyan-400', bgColor: 'bg-cyan-500/20' }
};

// Calculate timing status based on action timing requirements
export function calculateTimingStatus(elapsedSeconds, expectedWithinSeconds, criticalWithinSeconds) {
  if (!expectedWithinSeconds) {
    return TIMING_STATUS.ON_TIME; // No timing requirement
  }
  
  if (elapsedSeconds <= expectedWithinSeconds) {
    return TIMING_STATUS.ON_TIME;
  } else if (elapsedSeconds <= expectedWithinSeconds * 1.5) {
    return TIMING_STATUS.LATE;
  } else if (criticalWithinSeconds && elapsedSeconds <= criticalWithinSeconds) {
    return TIMING_STATUS.CRITICAL;
  } else {
    return TIMING_STATUS.OUT_OF_WINDOW;
  }
}

// Format time as MM:SS
function formatTime(seconds) {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
}

// Individual activity item component
function ActivityItem({ activity, isExpanded }) {
  const timing = activity.timing_status || TIMING_STATUS.ON_TIME;
  const TimingIcon = timing.icon;
  const roleStyle = ROLE_STYLES[activity.role] || ROLE_STYLES.medico;
  const isTeamAction = activity.actor === 'equipo' || activity.event_type === 'accion_equipo';
  
  return (
    <div 
      className={`relative p-2 rounded-lg border ${timing.borderColor} ${timing.bgLight} transition-all`}
      data-testid={`activity-item-${activity.id}`}
    >
      <div className="flex items-start gap-2">
        {/* Timing indicator */}
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className={`flex-shrink-0 w-6 h-6 rounded-full ${timing.color} flex items-center justify-center`}>
                <TimingIcon size={14} className="text-white" weight="bold" />
              </div>
            </TooltipTrigger>
            <TooltipContent side="left" className="bg-slate-800 border-slate-700">
              <p className="text-xs">{timing.label}: {timing.description}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        
        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            {/* Time */}
            <span className="font-mono text-xs text-slate-400 flex items-center gap-1">
              <Timer size={12} />
              {formatTime(activity.tiempo || activity.elapsed_seconds || 0)}
            </span>
            
            {/* Timing badge */}
            <Badge className={`${timing.bgLight} ${timing.textColor} text-[10px] px-1.5 py-0`}>
              {timing.label}
            </Badge>
            
            {/* Role badge */}
            <Badge className={`${roleStyle.bgColor} ${roleStyle.color} text-[10px] px-1.5 py-0 flex items-center gap-1`}>
              {isTeamAction ? <Users size={10} /> : <User size={10} />}
              {isTeamAction ? 'Equipo' : roleStyle.label}
            </Badge>
          </div>
          
          {/* Action name */}
          <div className="flex items-center gap-1 mt-1">
            {activity.dosis ? (
              <Pill size={14} className="text-purple-400 flex-shrink-0" />
            ) : (
              <FirstAid size={14} className="text-blue-400 flex-shrink-0" />
            )}
            <span className="text-sm text-white font-medium truncate">
              {activity.nombre || activity.action}
            </span>
          </div>
          
          {/* Details (expanded view) */}
          {isExpanded && (
            <div className="mt-2 text-xs text-slate-400 space-y-1">
              {activity.dosis && (
                <p>Dosis: {activity.dosis} {activity.unidad} vía {activity.via}</p>
              )}
              {activity.detalles && (
                <p>Detalles: {activity.detalles}</p>
              )}
              {activity.is_adverse_event && (
                <p className="text-red-400 flex items-center gap-1">
                  <Warning size={12} />
                  Evento Adverso: {activity.adverse_reason}
                </p>
              )}
              {activity.vitals_snapshot && (
                <p className="flex items-center gap-1">
                  <Heartbeat size={12} />
                  FC: {activity.vitals_snapshot.fc} | SpO2: {activity.vitals_snapshot.spo2}% | 
                  PA: {activity.vitals_snapshot.pa_sistolica}/{activity.vitals_snapshot.pa_diastolica}
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Compact activity log (for sidebar)
export function CompactActivityLog({ activities, formatTime, maxItems = 8 }) {
  const sortedActivities = useMemo(() => {
    return [...activities]
      .sort((a, b) => (b.tiempo || b.elapsed_seconds || 0) - (a.tiempo || a.elapsed_seconds || 0))
      .slice(0, maxItems);
  }, [activities, maxItems]);

  return (
    <div className="space-y-1.5">
      {sortedActivities.map((activity, i) => {
        const timing = activity.timing_status || TIMING_STATUS.ON_TIME;
        const isTeamAction = activity.actor === 'equipo' || activity.event_type === 'accion_equipo';
        
        return (
          <div 
            key={activity.id || i} 
            className={`flex items-center gap-1.5 text-xs p-1.5 rounded ${timing.bgLight} border-l-2 ${timing.borderColor}`}
          >
            {/* Time */}
            <span className="font-mono text-[10px] text-slate-500 w-10 flex-shrink-0">
              {formatTime(activity.tiempo || activity.elapsed_seconds || 0)}
            </span>
            
            {/* Timing dot */}
            <span className={`w-2 h-2 rounded-full ${timing.color} flex-shrink-0`} />
            
            {/* Icon */}
            {activity.dosis ? (
              <Pill size={12} className="text-purple-400 flex-shrink-0" />
            ) : isTeamAction ? (
              <Users size={12} className="text-cyan-400 flex-shrink-0" />
            ) : (
              <FirstAid size={12} className="text-blue-400 flex-shrink-0" />
            )}
            
            {/* Name */}
            <span className="text-slate-300 truncate flex-1">
              {activity.nombre || activity.action}
            </span>
            
            {/* Adverse event indicator */}
            {activity.is_adverse_event && (
              <Warning size={12} className="text-red-400 flex-shrink-0" />
            )}
          </div>
        );
      })}
      
      {activities.length === 0 && (
        <p className="text-slate-500 text-[10px] text-center py-2">Sin actividad</p>
      )}
    </div>
  );
}

// Full Activity Log Panel (expandable)
export default function ActivityLogPanel({ 
  activities, 
  timeline = [],
  teamNotifications = [],
  clinicalRole,
  elapsedTime,
  formatTime: formatTimeFn,
  isExpanded = false,
  onToggleExpand
}) {
  const [expanded, setExpanded] = useState(isExpanded);
  const [showDetails, setShowDetails] = useState(false);
  const scrollRef = useRef(null);
  
  // Combine all activities
  const allActivities = useMemo(() => {
    const combined = [];
    
    // Add medications and actions
    activities.forEach(a => {
      combined.push({
        ...a,
        id: a.id || `act-${a.tiempo}-${a.nombre}`,
        timing_status: a.timing_status || calculateTimingStatus(a.tiempo, a.expected_within, a.critical_within)
      });
    });
    
    // Add timeline events
    timeline.forEach(t => {
      if (!combined.find(c => c.id === t.id)) {
        combined.push({
          ...t,
          tiempo: t.elapsed_seconds,
          nombre: t.action,
          timing_status: t.timing_status || TIMING_STATUS.ON_TIME
        });
      }
    });
    
    // Add team notifications
    teamNotifications.forEach(n => {
      combined.push({
        ...n,
        tiempo: n.elapsed_seconds,
        nombre: n.message,
        actor: 'equipo',
        event_type: 'accion_equipo',
        timing_status: TIMING_STATUS.ON_TIME
      });
    });
    
    return combined.sort((a, b) => (b.tiempo || 0) - (a.tiempo || 0));
  }, [activities, timeline, teamNotifications]);

  // Auto-scroll to latest activity
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  }, [allActivities.length]);

  // Stats
  const stats = useMemo(() => {
    const onTime = allActivities.filter(a => a.timing_status?.id === 'on_time').length;
    const late = allActivities.filter(a => a.timing_status?.id === 'late').length;
    const critical = allActivities.filter(a => a.timing_status?.id === 'critical').length;
    const outOfWindow = allActivities.filter(a => a.timing_status?.id === 'out_of_window').length;
    const adverse = allActivities.filter(a => a.is_adverse_event).length;
    
    return { total: allActivities.length, onTime, late, critical, outOfWindow, adverse };
  }, [allActivities]);

  const handleToggle = () => {
    const newExpanded = !expanded;
    setExpanded(newExpanded);
    onToggleExpand?.(newExpanded);
  };

  const roleStyle = ROLE_STYLES[clinicalRole] || ROLE_STYLES.medico;

  return (
    <Card className={`bg-slate-800 border-slate-700 transition-all duration-300 ${expanded ? 'h-auto' : 'h-auto'}`}>
      {/* Header */}
      <CardHeader className="p-3 pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm text-white flex items-center gap-2">
            <Clock size={16} className="text-blue-400" />
            Registro de Actividades
            <Badge variant="outline" className="text-[10px] border-slate-600">
              {stats.total}
            </Badge>
          </CardTitle>
          
          <div className="flex items-center gap-2">
            {/* Current role */}
            <Badge className={`${roleStyle.bgColor} ${roleStyle.color} text-[10px]`}>
              {roleStyle.label}
            </Badge>
            
            {/* Expand/Collapse button */}
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={handleToggle}
              className="h-6 w-6 p-0"
            >
              {expanded ? <ArrowsInSimple size={14} /> : <ArrowsOutSimple size={14} />}
            </Button>
          </div>
        </div>
        
        {/* Stats bar */}
        <div className="flex items-center gap-2 mt-2">
          <TooltipProvider>
            {[
              { status: TIMING_STATUS.ON_TIME, count: stats.onTime },
              { status: TIMING_STATUS.LATE, count: stats.late },
              { status: TIMING_STATUS.CRITICAL, count: stats.critical },
              { status: TIMING_STATUS.OUT_OF_WINDOW, count: stats.outOfWindow }
            ].map(({ status, count }) => (
              <Tooltip key={status.id}>
                <TooltipTrigger asChild>
                  <div className={`flex items-center gap-1 px-1.5 py-0.5 rounded ${status.bgLight}`}>
                    <span className={`w-2 h-2 rounded-full ${status.color}`} />
                    <span className={`text-[10px] ${status.textColor}`}>{count}</span>
                  </div>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="bg-slate-800 border-slate-700">
                  <p className="text-xs">{status.label}</p>
                </TooltipContent>
              </Tooltip>
            ))}
            
            {stats.adverse > 0 && (
              <div className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-red-500/10">
                <Warning size={10} className="text-red-400" />
                <span className="text-[10px] text-red-400">{stats.adverse}</span>
              </div>
            )}
          </TooltipProvider>
        </div>
      </CardHeader>
      
      <CardContent className="p-3 pt-0">
        {expanded ? (
          /* Expanded view */
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowDetails(!showDetails)}
                className="text-xs text-slate-400 h-6 px-2"
              >
                {showDetails ? 'Ocultar detalles' : 'Mostrar detalles'}
                {showDetails ? <CaretUp size={12} className="ml-1" /> : <CaretDown size={12} className="ml-1" />}
              </Button>
            </div>
            
            <ScrollArea className="h-[300px]" ref={scrollRef}>
              <div className="space-y-2 pr-2">
                {allActivities.map((activity, i) => (
                  <ActivityItem 
                    key={activity.id || i} 
                    activity={activity} 
                    isExpanded={showDetails}
                  />
                ))}
                
                {allActivities.length === 0 && (
                  <div className="text-center py-8 text-slate-400">
                    <Clock size={32} className="mx-auto mb-2 opacity-50" />
                    <p className="text-sm">Sin actividad registrada</p>
                    <p className="text-xs">Las acciones aparecerán aquí</p>
                  </div>
                )}
              </div>
            </ScrollArea>
          </div>
        ) : (
          /* Compact view */
          <ScrollArea className="h-28">
            <CompactActivityLog 
              activities={allActivities} 
              formatTime={formatTimeFn || formatTime}
              maxItems={6}
            />
          </ScrollArea>
        )}
      </CardContent>
    </Card>
  );
}
